import React from "react";
import { useTranslation } from "react-i18next";
import { motion, useScroll, useTransform } from "framer-motion";
import CurrencyConverter from "./CurrencyConverter";
import heroImg from "../assets/elementor-widget.webp";
import { useTextScramble, EXPO_OUT, clipRevealUp } from "../utils/animations";

export default function Hero() {
  const { t } = useTranslation();
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 600], [0, 150]);
  const contentOpacity = useTransform(scrollY, [0, 400], [1, 0]);
  const contentY = useTransform(scrollY, [0, 400], [0, 60]);

  /* Text scramble on company name — cinematic reveal */
  const scrambledName = useTextScramble(t("hero.company"), 600, 28);

  return (
    <section
      id="hero"
      className="relative min-h-screen flex flex-col items-center justify-start overflow-hidden"
    >
      {/* Parallax background with scroll */}
      <motion.div
        className="absolute inset-0 bg-cover bg-center will-change-transform"
        style={{ backgroundImage: `url(${heroImg})`, y: bgY }}
      />
      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-main/85 via-main/65 to-white dark:to-main" />

      {/* Gradient mesh overlay for depth */}
      <div className="absolute inset-0 gradient-mesh opacity-60" />

      {/* Animated decorative orbs */}
      <div className="absolute top-1/4 start-1/4 w-96 h-96 bg-highlight/8 rounded-full blur-[80px] animate-float-orb" />
      <div className="absolute bottom-1/3 end-1/4 w-80 h-80 bg-highlight/6 rounded-full blur-[60px] animate-float-orb-delayed" />
      <div className="absolute top-1/2 end-1/3 w-64 h-64 bg-white/5 rounded-full blur-[50px] animate-float-orb" style={{ animationDelay: "-5s" }} />

      {/* Noise texture */}
      <div className="absolute inset-0 noise-overlay opacity-30" />

      {/* Content */}
      <motion.div
        style={{ opacity: contentOpacity, y: contentY }}
        className="relative z-10 flex flex-col items-center w-full max-w-2xl mx-auto px-4 pt-28 pb-16 flex-1 justify-center"
      >
        {/* Licensed badge — clip reveal */}
        <motion.div
          variants={clipRevealUp}
          initial="hidden"
          animate="visible"
          custom={0}
          className="flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-full px-5 py-2.5 mb-8 hover:bg-white/15 hover:border-white/30 transition-colors cursor-default group"
        >
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-400 shadow-[0_0_8px_rgba(74,222,128,0.5)]" />
          </span>
          <span className="text-white/90 font-cairo text-sm font-medium">
            {t("hero.badge")}
          </span>
        </motion.div>

        {/* Company name — text scramble reveal */}
        <motion.h1
          initial={{ opacity: 0, y: 30, filter: "blur(12px)" }}
          animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          transition={{ duration: 1, delay: 0.3, ease: EXPO_OUT }}
          className="text-4xl md:text-6xl lg:text-7xl font-extrabold text-white font-syne leading-tight text-center mb-5"
        >
          <span className="bg-gradient-to-l from-white via-white to-highlight/80 bg-clip-text text-transparent">
            {scrambledName}
          </span>
        </motion.h1>

        {/* Tagline — fade in with blur */}
        <motion.p
          initial={{ opacity: 0, y: 25, filter: "blur(8px)" }}
          animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          transition={{ duration: 0.8, delay: 1.2, ease: EXPO_OUT }}
          className="text-base md:text-lg text-highlight font-cairo text-center max-w-xl mb-12"
        >
          {t("hero.tagline")}
        </motion.p>

        {/* Currency converter — scale + blur entrance */}
        <motion.div
          initial={{ opacity: 0, y: 50, scale: 0.92, filter: "blur(12px)" }}
          animate={{ opacity: 1, y: 0, scale: 1, filter: "blur(0px)" }}
          transition={{ duration: 0.9, delay: 1.5, ease: EXPO_OUT }}
          className="w-full max-w-lg"
        >
          <CurrencyConverter />
        </motion.div>
      </motion.div>

      {/* Bottom fade */}
      <div className="absolute bottom-0 inset-x-0 h-40 bg-gradient-to-t from-white dark:from-main via-white/80 dark:via-main/80 to-transparent" />
    </section>
  );
}
